import CountryDropdown from "components/dropdowns/reactselect/CountryDropdown";
import StateDropdown from "components/dropdowns/reactselect/StateDropdown";
import { StatusDropdown } from "components";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import React, { useCallback } from "react";
import { useCustomQuery } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";
import { Message } from "semantic-ui-react";
import { PhoneInput } from "components/PhoneInput";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { isNullOrEmptyString } from "shared/utils";
import MultiEmailInput from "components/ValidatedFields/MultiEmailInput";
import { useOpenSidePanel } from "../SidePanel";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import { ContactName } from "contact/detail/components/ContactName";
import { useOpenContactSidePanel } from "./ContactSidePanel";
import TagsSelect from "components/Tag/TagsSelect";
import { AfterCreateAction } from "../types";
import { useClientId } from "common/SessionContext";
import { getCreateContactInstruction, getInitialNewContactFormState, NewContactFormState } from "shared/utils/fieldsHelper/newContactFields";

interface Props {
  onCreated: (contact: server.dto.Contact) => void;
  statuses: server.dto.StatusSmall[];
  afterCreate?: AfterCreateAction;
}

const NewContactSidePanel = (props: Props) => {

  const clientId = useClientId();

  const openContactDetailSidePanel = useOpenContactSidePanel();
  const afterCreate = props.afterCreate ?? "displayItemInSidebar";
  const [formState, setFormState] = React.useState<NewContactFormState>(getInitialNewContactFormState(serverReference, site.userPreference, props.statuses));

  const onChange = (newData: Partial<NewContactFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }

  const onSuccess = async (contactId: number) => {
    const res = await window.CoreApi.Contact.GetSidePanelData(contactId).then(r => r.data);
    props.onCreated(res.data.contact);
  }

  const debouncedContactName = useDebounce(
    [formState.firstName, formState.insertion, formState.lastName]
      .map(n => n.trim())
      .filter(n => !isNullOrEmptyString(n))
      .join(" "));

  const contactExistsQuery = useCustomQuery({
    queryKey: ["contactExists", debouncedContactName],
    queryFn: () => window.CoreApi.Contact.NameExists({
      name: debouncedContactName
    }).then(r => r.data),
    enabled: Boolean(debouncedContactName),
  });

  const submit = () => {
    return window.CoreApi.Contact.Create(getCreateContactInstruction(formState));
  };

  return <SidePanelNewSectionTemplate
    onSave={(id) => {
      onSuccess(id);
      if (afterCreate === "displayItemInSidebar") {
        openContactDetailSidePanel(id);
      }
    }}
    afterCreate={afterCreate}
    title={site.currentEnvironment.contact.newLabel}
    id="new-contact-side-panel"
    trigger={undefined}
    submit={submit}
    sections={[
      {
        key: "contactExists",
        type: "custom",
        hidden: !contactExistsQuery.data?.data,
        render: () => <Message color="red" className="margin-top margin-bottom">
          {RESX.Contact.AContactWithThisNameAlreadyExists}
        </Message>
      },
      {
        key: "contactMainDetails",
        type: "regular",
        hideTitle: true,
        title: RESX.Contact.resxContact,
        fields: [
          {
            key: "name",
            label: RESX.GeneralLabels.Name,
            render: () => (
              <ContactName
                firstName={formState.firstName}
                insertion={formState.insertion}
                lastName={formState.lastName}
                onChange={(firstName, insertion, lastName) => onChange({
                  firstName,
                  insertion,
                  lastName
                })}
              />
            )
          }, {
            key: "emailAddresses",
            label: RESX.GeneralLabels.Email,
            render: () => (
              <MultiEmailInput
                value={formState.emailAddresses}
                onChange={(value) => onChange({ emailAddresses: value })}
                validatorKey="contactEmails"
                maxLength={serverReference.constants.mailAddress.maxEmailLength}
              />
            )
          }, {
            key: "mobileNumbers",
            label: RESX.Contact.Mobile,
            render: () => (
              <PhoneInput
                value={formState.mobileNumbers}
                onChange={(value) => onChange({ mobileNumbers: value })}
              />
            )
          }, {
            key: "language",
            label: RESX.Language.resxLanguage,
            render: () => (
              <CulturesTemplateDropdown
                value={formState.language}
                onChange={(value) => onChange({ language: value })}
              />
            )
          }, {
            key: "position",
            label: RESX.Contact.Position,
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.contact.maxPositionLength}
                value={formState.position}
                onChange={(value) => onChange({ position: value })}
              />
            )
          }, {
            key: "status",
            label: RESX.Status.resxStatus,
            hidden: props.statuses.length <= 1,
            render: () => (
              <StatusDropdown
                mainArea="contact"
                clientId={clientId}
                value={formState.status}
                options={undefined}
                onChange={(value) => onChange({ status: value })}
              />
            )
          }, {
            key: "tags",
            label: RESX.Tag.Tags,
            render: () => <TagsSelect
              mainArea="contact"
              value={formState.tags}
              onChange={(tags) => onChange({ tags })}
              inPageEditing="new"
            />
          }
        ]
      },
      {
        key: "address",
        type: "regular",
        title: RESX.Address.resxAddress,
        fields: [
          {
            key: "addressLine1",
            label: RESX.Address.AddressLine + " 1",
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.address.maxAddressLineLength}
                value={formState.addressLine1}
                onChange={(value) => onChange({ addressLine1: value })}
              />
            )
          }, {
            key: "addressLine2",
            label: RESX.Address.AddressLine + " 2",
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.address.maxAddressLineLength}
                value={formState.addressLine2}
                onChange={(value) => onChange({ addressLine2: value })}
              />
            )
          }, {
            key: "postalCode",
            label: RESX.Address.PostalCode,
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.address.maxPostalCodeLength}
                value={formState.postalCode}
                onChange={(value) => onChange({ postalCode: value })}
              />
            )
          }, {
            key: "city",
            label: RESX.Address.City,
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.address.maxCityLength}
                value={formState.city}
                onChange={(value) => onChange({ city: value })}
              />
            )
          }, {
            key: "country",
            label: RESX.Address.Country,
            render: () => (
              <CountryDropdown
                value={formState.country}
                onChange={(value) => onChange({
                  country: value,
                  state: value.states.find(s => s.isCountryDefault)
                })}
              />
            )
          }, {
            key: "state",
            label: RESX.Address.State,
            render: () => (
              <StateDropdown
                value={formState.state}
                options={formState.country?.states ?? []}
                onChange={(value) => onChange({ state: value })}
              />
            )
          }
        ]
      },
      {
        key: "contactOtherDetails",
        type: "regular",
        title: RESX.Company.OtherDetails,
        fields: [
          {
            key: "socialSecurity",
            label: RESX.Contact.SocialSecurity,
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.contact.maxSocialSecurityLength}
                value={formState.socialSecurity}
                onChange={(value) => onChange({ socialSecurity: value })}
              />
            )
          }
        ]
      }
    ]}
  />
}

export const useOpenNewContactSidePanel = () => {
  const openSidePanel = useOpenSidePanel();
  const clientId = useClientId();

  const openNewContactSidePanel = useCallback(async (props: Omit<Props, "statuses">) => {
    const res = await window.CoreApi.Status.ListSmall({ clientId, mainArea: "contact" });
    openSidePanel(
      <NewContactSidePanel {...props} statuses={res.data.data} />,
      "new-contact-side-panel",
      "large"
    );
  }, [openSidePanel, clientId]);

  return openNewContactSidePanel;
}